import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import { getSavedPropertiesFromAPI, toggleSavedPropertyAPI } from '../utils/savedProperties';
import api from '../utils/api';
import { mapBackendProperties } from '../utils/propertyMapper';

// Placeholder for background image - replace with actual image path
const backgroundImg = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1920&q=80';

const Home = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { user } = useAuth();
  const { formatPrice, language } = useLanguageCurrency();
  const { t } = useTranslation();

  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchFeatured = async () => {
      try {
        const response = await api.get('/api/properties/featured', { params: { limit: 20 } });
        const list = response.data?.properties;
        if (!cancelled && Array.isArray(list)) {
          setFeaturedProperties(mapBackendProperties(list, language || 'en'));
        }
      } catch (err) {
        if (!cancelled) setFeaturedProperties([]);
      } finally {
        if (!cancelled) setLoadingFeatured(false);
      }
    };
    fetchFeatured();
    return () => { cancelled = true; };
  }, [language]);

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [location, setLocation] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [selectingDate, setSelectingDate] = useState(null); // 'checkIn' or 'checkOut' or null
  const [copiedPropertyId, setCopiedPropertyId] = useState(null);
  const [savedProperties, setSavedProperties] = useState(new Set());

  // Auto-focus checkout date after check-in is selected
  useEffect(() => {
    if (checkIn && !checkOut) {
      const timer = setTimeout(() => {
        const checkOutInputMobile = document.getElementById('checkOutModalMobile');
        const checkOutInputDesktop = document.getElementById('checkOutModalDesktop');
        const checkOutInput = checkOutInputMobile || checkOutInputDesktop;
        if (checkOutInput) {
          checkOutInput.focus();
          checkOutInput.click();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [checkIn, checkOut]);

  const propertyTypes = ['Apartment', 'Villa', 'Studio', 'Vacation Home'];
  
  const locationOptions = [
    'Cairo, Egypt',
    'Alexandria, Egypt',
    'Giza, Egypt',
    'Sharm El Sheikh, Egypt',
    'North Coast, Egypt',
    'Marsa Alam, Egypt',
    'New Cairo, Egypt',
    'Zamalek, Cairo',
    'Maadi, Cairo',
    'Heliopolis, Cairo',
    'Nasr City, Cairo',
    '6th of October, Giza'
  ];
  
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    if (value.length > 0) {
      const filtered = locationOptions.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setLocationSuggestions(filtered);
      setShowLocationSuggestions(true);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };
  
  const selectLocation = (loc) => {
    setLocation(loc);
    setShowLocationSuggestions(false);
  };
  

  // Load saved property IDs from backend when user is client
  useEffect(() => {
    if (!user) {
      setSavedProperties(new Set());
      return;
    }
    let cancelled = false;
    getSavedPropertiesFromAPI()
      .then(({ savedPropertyIds }) => {
        if (!cancelled) setSavedProperties(new Set((savedPropertyIds || []).map(String)));
      })
      .catch(() => { if (!cancelled) setSavedProperties(new Set()); });
    return () => { cancelled = true; };
  }, [user]);

  // Close location suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationSuggestions && !event.target.closest('.location-input-container')) {
        setShowLocationSuggestions(false);
      }
    };
    if (showLocationSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLocationSuggestions]);

  // Auto-focus check-out input after check-in is selected (both mobile and desktop)
  useEffect(() => {
    if (checkIn && !checkOut) {
      const timer = setTimeout(() => {
        const checkOutInputMobile = document.getElementById('checkOutModalMobile');
        const checkOutInputDesktop = document.getElementById('checkOutModalDesktop');
        const checkOutInput = checkOutInputMobile || checkOutInputDesktop;
        if (checkOutInput) {
          checkOutInput.focus();
          checkOutInput.click();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [checkIn, checkOut]);

  // Helper function to format dates nicely
  const formatDateRange = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayName}, ${month} ${day}`;
  };

  const calculateNights = () => {
    if (checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const scrollContainerRef = useRef(null);
  const testimonialsScrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canScrollLeftTestimonials, setCanScrollLeftTestimonials] = useState(false);
  const [canScrollRightTestimonials, setCanScrollRightTestimonials] = useState(true);
  const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
  const [hoverVideoPosition, setHoverVideoPosition] = useState({ x: 0, y: 0 });
  const hoverVideoRef = useRef(null);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial value
    setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update scroll button visibility for featured properties
  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < maxScroll - 1);
  };

  // Update scroll button visibility for testimonials
  const updateScrollButtonsTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollLeftTestimonials(container.scrollLeft > 0);
    setCanScrollRightTestimonials(container.scrollLeft < maxScroll - 1);
  };

  // Scroll handlers
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 320;
    container.scrollBy({ left: -cardWidth - 24, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 320;
    container.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
  };

  const scrollLeftTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 400;
    container.scrollBy({ left: -cardWidth - 24, behavior: 'smooth' });
  };

  const scrollRightTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 400;
    container.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
  };

  // Add scroll listeners and keep them when refs are set
  useEffect(() => {
    const container1 = scrollContainerRef.current;
    const container2 = testimonialsScrollRef.current;

    if (container1) {
      container1.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
    }
    if (container2) {
      container2.addEventListener('scroll', updateScrollButtonsTestimonials);
      updateScrollButtonsTestimonials();
    }

    return () => {
      if (container1) container1.removeEventListener('scroll', updateScrollButtons);
      if (container2) container2.removeEventListener('scroll', updateScrollButtonsTestimonials);
    };
  }, []);

  // Update featured scroll buttons when content changes (e.g. after load or when showing placeholders)
  useEffect(() => {
    if (loadingFeatured) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const timeoutId = setTimeout(updateScrollButtons, 150);
    const ro = new ResizeObserver(() => updateScrollButtons());
    ro.observe(container);
    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
    };
  }, [loadingFeatured, featuredProperties.length]);


  const handleSearch = (e) => {
    e.preventDefault();
    // Navigate to properties page with search params
    const params = new URLSearchParams();
    if (location) params.append('location', location);
    if (selectedPropertyType) params.append('type', selectedPropertyType);
    if (checkIn) params.append('checkIn', checkIn);
    if (checkOut) params.append('checkOut', checkOut);
    if (guests) params.append('guests', guests);
    navigate(`/properties?${params.toString()}`);
  };

  const handleShare = async (propertyId, e) => {
    e.stopPropagation();
    const property = featuredProperties.find(p => p.id === propertyId);
    const propertyUrl = `${window.location.origin}/properties/${propertyId}`;
    const shareData = {
      title: property ? property.title : 'Property',
      text: property ? `Check out this property: ${property.title}` : 'Check out this property',
      url: propertyUrl,
    };

    try {
      // Check if Web Share API is available
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(propertyUrl);
        setCopiedPropertyId(propertyId);
        setTimeout(() => {
          setCopiedPropertyId(null);
        }, 2000);
      }
    } catch (err) {
      // User cancelled or error occurred
      if (err.name !== 'AbortError') {
        // If not user cancellation, try clipboard fallback
        try {
          await navigator.clipboard.writeText(propertyUrl);
          setCopiedPropertyId(propertyId);
          setTimeout(() => {
            setCopiedPropertyId(null);
          }, 2000);
        } catch (clipboardErr) {
          console.error('Failed to share:', clipboardErr);
        }
      }
    }
  };

  return (
    <>
      <Navbar />
    <div className="min-h-screen text-white" style={{ backgroundColor: '#1D2A3B', width: '100%', margin: 0, padding: 0, position: 'static', top: 0, left: 0, right: 0, borderTop: 'none', overflow: 'hidden' }}>
      
      {/* Mobile spacer under navbar */}
      <div className="block sm:hidden" style={{ height: '0.0625rem' }}></div>

      {/* Hero Section */}
      <section
        className="relative w-full bg-cover bg-center bg-no-repeat pt-16 sm:pt-0 min-h-[60vh] sm:min-h-[80vh]"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(${backgroundImg})`,
          width: '100%',
          height: 'auto',
          paddingBottom: '2rem',
          overflow: 'hidden',
          marginTop: '64px',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          boxSizing: 'border-box'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/50" style={{ top: 0, pointerEvents: 'none', zIndex: 1 }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.15)_100%)]" style={{ top: 0, pointerEvents: 'none', zIndex: 1 }} />
        
        {/* Centered Text Content */}
        <div className="relative w-full px-4 sm:px-6 lg:px-8 flex items-center justify-center flex-1 pt-8 sm:pt-0" style={{ zIndex: 10 }}>
          <div className="w-full max-w-5xl mx-auto text-center" style={{ zIndex: 10 }}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in-up drop-shadow-2xl" style={{ color: '#FFFFFF', zIndex: 10, position: 'relative' }}>
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl font-light animate-fade-in-up animation-delay-200 drop-shadow-lg mb-8" style={{ color: '#FFFFFF', zIndex: 10, position: 'relative' }}>
              {t('home.heroSubtitle')}
            </p>
            
            {/* Compact Search Bar with All Filters - Single Row */}
            <div className="mt-12 sm:mt-20 mb-[-60px] max-w-6xl mx-auto relative z-20">
              {/* Mobile: Search Icon Button */}
              <div className="sm:hidden flex justify-center mb-4">
                <button
                  type="button"
                  onClick={() => setShowSearchModal(true)}
                  className="bg-slate-800/95 backdrop-blur-xl rounded-full shadow-2xl border border-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 p-4 hover:scale-110"
                  style={{ 
                    width: '64px', 
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>

              {/* Desktop: Full Search Form */}
              <form onSubmit={handleSearch} className="relative hidden sm:block">
                <div 
                  className="relative rounded-2xl overflow-hidden transition-all duration-300"
                  style={{
                    background: 'rgba(29, 42, 59, 0.95)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(213, 181, 119, 0.3)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.2)'
                  }}
                >
                  <div className="p-3 sm:p-4">
                    {/* All Fields in Single Row */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      {/* Location */}
                      <div className="relative flex-1">
                        <div className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                          <span className="text-sm sm:text-base">📍</span>
                        </div>
                        <select
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className="w-full pl-8 sm:pl-10 pr-6 sm:pr-8 py-2.5 sm:py-3 text-xs sm:text-sm text-white rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none transition-all"
                          style={{ 
                            backgroundColor: 'rgba(29, 42, 59, 0.6)',
                            border: '1px solid rgba(213, 181, 119, 0.2)'
                          }}
                        >
                          <option value="" style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>{t('home.location') || 'Location'}</option>
                          {locationOptions.map((loc) => (
                            <option key={loc} value={loc} style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>
                              {loc}
                            </option>
                          ))}
                        </select>
                        <svg className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 pointer-events-none" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Combined Check-in and Check-out Date Range */}
                      <div 
                        className="relative flex-1 rounded-lg sm:rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                        style={{ 
                          backgroundColor: selectingDate ? 'rgba(29, 42, 59, 0.8)' : 'rgba(29, 42, 59, 0.6)',
                          border: selectingDate ? '1px solid #D5B577' : '1px solid rgba(213, 181, 119, 0.2)',
                          boxShadow: selectingDate ? '0 0 0 3px rgba(213, 181, 119, 0.1), 0 4px 12px rgba(213, 181, 119, 0.2)' : 'none',
                          transform: selectingDate ? 'scale(1.01)' : 'scale(1)',
                          minHeight: '44px',
                          padding: '10px 0'
                        }}
                        onClick={(e) => {
                          if (!e.target.closest('button') && !e.target.closest('input')) {
                            if (!checkIn) {
                              const checkInInput = document.getElementById('checkInHero');
                              if (checkInInput) {
                                checkInInput.focus();
                                checkInInput.click();
                              }
                            } else if (!checkOut) {
                              const checkOutInput = document.getElementById('checkOutHero');
                              if (checkOutInput) {
                                checkOutInput.focus();
                                checkOutInput.click();
                              }
                            } else {
                              const checkInInput = document.getElementById('checkInHero');
                              if (checkInInput) {
                                checkInInput.focus();
                                checkInInput.click();
                              }
                            }
                          }
                        }}
                      >
                        <div className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                          <span className="text-sm sm:text-base">📅</span>
                        </div>
                        {/* Display text overlay */}
                        <div 
                          className="absolute left-8 sm:left-10 right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none z-50 transition-all duration-300"
                          style={{
                            opacity: selectingDate ? 0.7 : 1,
                            color: '#FFFFFF'
                          }}
                        >
                          {checkIn && checkOut ? (
                            <div className="text-xs sm:text-sm flex items-center gap-1" style={{ color: '#FFFFFF' }}>
                              <span className="font-semibold truncate">{formatDateRange(checkIn)}</span>
                              <span style={{ color: '#D5B577' }}>→</span>
                              <span className="font-semibold truncate">{formatDateRange(checkOut)}</span>
                            </div>
                          ) : checkIn ? (
                            <div className="text-xs sm:text-sm flex items-center" style={{ color: '#FFFFFF' }}>
                              <span className="font-semibold truncate">{formatDateRange(checkIn)}</span>
                              <span className="ml-1 text-xs hidden sm:inline" style={{ color: '#9CA3AF' }}>
                                → {language === 'ar' ? 'اختر' : 'Checkout'}
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs sm:text-sm truncate" style={{ color: '#9CA3AF' }}>
                              {t('home.checkIn') || 'Check-in'}
                            </div>
                          )}
                        </div>
                        {/* Hidden date inputs */}
                        {!checkIn && (
                          <input
                            id="checkInHero"
                            type="date"
                            value={checkIn}
                            onChange={(e) => {
                              setCheckIn(e.target.value);
                              setSelectingDate('checkOut');
                            }}
                            onFocus={() => setSelectingDate('checkIn')}
                            onBlur={() => {
                              setTimeout(() => {
                                setSelectingDate(null);
                              }, 200);
                            }}
                            className="absolute inset-0 w-full h-full cursor-pointer z-30"
                            style={{ 
                              backgroundColor: 'transparent',
                              border: 'none',
                              outline: 'none',
                              padding: 0,
                              margin: 0,
                              opacity: 0.01,
                              color: 'transparent',
                              pointerEvents: 'auto'
                            }}
                          />
                        )}
                        {checkIn && !checkOut && (
                          <input
                            id="checkOutHero"
                            type="date"
                            value={checkOut}
                            onChange={(e) => {
                              setCheckOut(e.target.value);
                              setSelectingDate(null);
                            }}
                            min={checkIn || undefined}
                            onFocus={() => setSelectingDate('checkOut')}
                            onBlur={() => setSelectingDate(null)}
                            className="absolute inset-0 w-full h-full cursor-pointer z-30"
                            style={{ 
                              backgroundColor: 'transparent',
                              border: 'none',
                              outline: 'none',
                              padding: 0,
                              margin: 0,
                              opacity: 0.01,
                              color: 'transparent',
                              pointerEvents: 'auto'
                            }}
                          />
                        )}
                        {checkIn && checkOut && (
                          <input
                            id="checkInHero"
                            type="date"
                            value={checkIn}
                            onChange={(e) => {
                              setCheckIn(e.target.value);
                              setSelectingDate('checkOut');
                            }}
                            onFocus={() => setSelectingDate('checkIn')}
                            onBlur={() => {
                              setTimeout(() => {
                                setSelectingDate(null);
                              }, 200);
                            }}
                            className="absolute inset-0 w-full h-full cursor-pointer z-30"
                            style={{ 
                              backgroundColor: 'transparent',
                              border: 'none',
                              outline: 'none',
                              padding: 0,
                              margin: 0,
                              opacity: 0.01,
                              color: 'transparent',
                              pointerEvents: 'auto'
                            }}
                          />
                        )}
                      </div>

                      {/* Guests */}
                      <div className="relative flex-1">
                        <div className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                          <span className="text-sm sm:text-base">👤</span>
                        </div>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={guests}
                          onChange={(e) => setGuests(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                          placeholder={t('home.guests') || 'Guests'}
                          className="w-full pl-8 sm:pl-10 pr-3 py-2.5 sm:py-3 text-xs sm:text-sm text-white rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D5B577] transition-all placeholder-gray-400"
                          style={{ 
                            backgroundColor: 'rgba(29, 42, 59, 0.6)',
                            border: '1px solid rgba(213, 181, 119, 0.2)'
                          }}
                        />
                      </div>

                      {/* Property Type */}
                      <div className="relative flex-1">
                        <div className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                          <span className="text-sm sm:text-base">🏠</span>
                        </div>
                        <select
                          value={selectedPropertyType}
                          onChange={(e) => setSelectedPropertyType(e.target.value)}
                          className="w-full pl-8 sm:pl-10 pr-6 sm:pr-8 py-2.5 sm:py-3 text-xs sm:text-sm text-white rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none transition-all"
                          style={{ 
                            backgroundColor: 'rgba(29, 42, 59, 0.6)',
                            border: '1px solid rgba(213, 181, 119, 0.2)'
                          }}
                        >
                          <option value="" style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>{t('home.propertyType') || 'Type'}</option>
                          {propertyTypes.map((type) => (
                            <option key={type} value={type} style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>
                              {type}
                            </option>
                          ))}
                        </select>
                        <svg className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 pointer-events-none" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Search Button */}
                      <button
                        type="submit"
                        className="w-full sm:flex-shrink-0 sm:w-auto sm:min-w-[120px] py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-200 hover:scale-105 flex items-center justify-center gap-1 sm:gap-2"
                        style={{ 
                          backgroundColor: '#D5B577', 
                          color: '#1D2A3B',
                          boxShadow: '0 4px 12px rgba(213, 181, 119, 0.4)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#c4a366';
                          e.target.style.boxShadow = '0 6px 16px rgba(213, 181, 119, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#D5B577';
                          e.target.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.4)';
                        }}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span className="hidden sm:inline">{t('common.find')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Search Modal */}
        {showSearchModal && (
          <div 
            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.75)', 
              backdropFilter: 'blur(4px)',
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={() => setShowSearchModal(false)}
          >
            <div 
              className="bg-slate-800 shadow-2xl w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[95vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl"
              style={{ 
                border: '1px solid rgba(213, 181, 119, 0.3)',
                animation: window.innerWidth < 640 ? 'slideUp 0.3s ease-out' : 'scaleIn 0.2s ease-out',
                transform: window.innerWidth < 640 ? 'translateY(0)' : 'scale(1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Mobile: Drag Handle */}
              <div className="sm:hidden flex justify-center pt-3 pb-2 relative">
                <div 
                  className="w-12 h-1.5 bg-gray-500 rounded-full transition-all duration-300"
                  style={{
                    animation: 'pulse 2s ease-in-out infinite'
                  }}
                ></div>
                {/* Close Button - Positioned in drag handle area on mobile */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearchModal(false);
                  }}
                  className="absolute top-4 right-2 w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 active:bg-slate-500 flex items-center justify-center transition-all duration-200 z-50 group sm:hidden"
                  style={{ 
                    border: '1px solid rgba(213, 181, 119, 0.3)',
                    transform: 'scale(1)',
                    pointerEvents: 'auto',
                    touchAction: 'manipulation',
                    minWidth: '40px',
                    minHeight: '40px'
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    e.currentTarget.style.transform = 'scale(0.95)';
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.8)';
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.currentTarget.style.transform = 'scale(1)';
                    setShowSearchModal(false);
                  }}
                >
                  <svg className="w-5 h-5 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="relative p-4 sm:p-5 pb-6 sm:pb-5">
                {/* Close Button - Desktop only */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearchModal(false);
                  }}
                  className="hidden sm:flex absolute top-3 right-3 w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 active:bg-slate-500 items-center justify-center transition-all duration-200 z-10 group cursor-pointer"
                  style={{ 
                    border: '1px solid rgba(213, 181, 119, 0.3)',
                    transform: 'scale(1)',
                    minWidth: '40px',
                    minHeight: '40px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    e.currentTarget.style.backgroundColor = '';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.95)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
                  }}
                >
                  <svg className="w-5 h-5 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Modal Title - Hidden on mobile, shown on desktop */}
                <h2 className="hidden sm:block text-xl font-bold mb-4 text-white">
                  {t('home.search')}
                </h2>

                {/* Search Form */}
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch(e);
                  setShowSearchModal(false);
                }} className="space-y-3 sm:space-y-3 mt-6 sm:mt-0">
                  {/* Location Dropdown */}
                  <div className="relative location-input-container">
                    <label htmlFor="locationModal" className="hidden sm:block text-sm font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                      {t('home.location')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-transform duration-300"
                        style={{
                          transform: document.activeElement?.id === 'locationModal' ? 'scale(1.2) translateY(-50%)' : 'scale(1) translateY(-50%)'
                        }}
                      >
                        <span className="text-base sm:text-lg">📍</span>
                      </div>
                      <select
                        id="locationModal"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#D5B577';
                          e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                          e.target.style.transform = 'scale(1.01)';
                        }}
                        className="w-full pl-10 sm:pl-12 pr-10 py-3 sm:py-2.5 text-sm text-white rounded-xl sm:rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 appearance-none"
                        style={{ 
                          backgroundColor: 'rgba(29, 42, 59, 0.8)',
                          border: '1px solid rgba(213, 181, 119, 0.2)',
                          transition: 'all 0.3s ease'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                          e.target.style.boxShadow = 'none';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <option value="">{language === 'ar' ? 'الموقع' : 'Location'}</option>
                        {locationOptions.map((loc) => (
                          <option key={loc} value={loc}>
                            {loc}
                          </option>
                        ))}
                      </select>
                      <svg 
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>


                  {/* Check In and Check Out - Single field on mobile, two fields on desktop */}
                  {/* Mobile: Single Date Range Field */}
                  <div className="sm:hidden">
                    <div 
                      className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                      style={{ 
                        backgroundColor: selectingDate ? 'rgba(29, 42, 59, 0.95)' : 'rgba(29, 42, 59, 0.8)',
                        border: selectingDate ? '1px solid #D5B577' : '1px solid rgba(213, 181, 119, 0.2)',
                        boxShadow: selectingDate ? '0 0 0 3px rgba(213, 181, 119, 0.1), 0 4px 12px rgba(213, 181, 119, 0.2)' : 'none',
                        transform: selectingDate ? 'scale(1.01)' : 'scale(1)',
                        minHeight: '48px',
                        padding: '12px 0'
                      }}
                      onClick={(e) => {
                        // If clicking on the container (not the clear button), trigger the appropriate input
                        if (!e.target.closest('button')) {
                          if (!checkIn) {
                            const checkInInput = document.getElementById('checkInModalMobile');
                            if (checkInInput) {
                              checkInInput.focus();
                              checkInInput.click();
                            }
                          } else if (!checkOut) {
                            const checkOutInput = document.getElementById('checkOutModalMobile');
                            if (checkOutInput) {
                              checkOutInput.focus();
                              checkOutInput.click();
                            }
                          } else {
                            // Both dates selected, allow editing by clicking check-in again
                            const checkInInput = document.getElementById('checkInModalMobile');
                            if (checkInInput) {
                              checkInInput.focus();
                              checkInInput.click();
                            }
                          }
                        }
                      }}
                    >
                      <div 
                        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-all duration-300 flex items-center justify-center"
                        style={{
                          transform: selectingDate ? 'scale(1.2) rotate(5deg) translateY(-50%)' : 'scale(1) rotate(0deg) translateY(-50%)'
                        }}
                      >
                        <span className="text-base">📅</span>
                      </div>
                      {/* Display text overlay - always visible on top */}
                      <div 
                        className="absolute left-10 right-10 top-1/2 -translate-y-1/2 pointer-events-none z-50 transition-all duration-300"
                        style={{
                          opacity: selectingDate ? 0.7 : 1,
                          color: '#FFFFFF'
                        }}
                      >
                        {checkIn && checkOut ? (
                          <div className="text-sm flex items-center justify-between w-full" style={{ color: '#FFFFFF' }}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkIn)}</span>
                              <span 
                                className="transition-all duration-300"
                                style={{ 
                                  color: '#D5B577',
                                  animation: 'pulse 2s ease-in-out infinite'
                                }}
                              >→</span>
                              <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkOut)}</span>
                            </div>
                            <span className="text-xs ml-2" style={{ color: '#9CA3AF' }}>
                              {calculateNights()} {calculateNights() === 1 ? (language === 'ar' ? 'ليلة' : 'night') : (language === 'ar' ? 'ليالي' : 'nights')}
                            </span>
                          </div>
                        ) : checkIn ? (
                          <div className="text-sm flex items-center" style={{ color: '#FFFFFF' }}>
                            <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkIn)}</span>
                            <span 
                              className="ml-3 text-xs animate-pulse flex items-center gap-1"
                              style={{ animation: 'pulse 1.5s ease-in-out infinite', color: '#9CA3AF' }}
                            >
                              <span>→</span>
                              <span>{language === 'ar' ? 'اختر تاريخ المغادرة' : 'Select checkout date'}</span>
                            </span>
                          </div>
                        ) : (
                          <div className="text-sm flex items-center gap-2" style={{ color: '#9CA3AF' }}>
                            <span>{language === 'ar' ? 'التواريخ' : 'Dates'}</span>
                            <span className="text-xs opacity-80">({language === 'ar' ? 'انقر للاختيار' : 'Click to select'})</span>
                          </div>
                        )}
                      </div>
                      {/* Always render both inputs, always clickable - below text */}
                      {!checkIn && (
                        <input
                          id="checkInModalMobile"
                          type="date"
                          value={checkIn}
                          onChange={(e) => {
                            setCheckIn(e.target.value);
                            setSelectingDate('checkOut');
                          }}
                          onFocus={() => setSelectingDate('checkIn')}
                          onBlur={() => {
                            setTimeout(() => {
                              setSelectingDate(null);
                            }, 200);
                          }}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {checkIn && !checkOut && (
                        <input
                          id="checkOutModalMobile"
                          type="date"
                          value={checkOut}
                          onChange={(e) => {
                            setCheckOut(e.target.value);
                            setSelectingDate(null);
                          }}
                          min={checkIn || undefined}
                          onFocus={() => setSelectingDate('checkOut')}
                          onBlur={() => setSelectingDate(null)}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {checkIn && checkOut && (
                        <input
                          id="checkInModalMobile"
                          type="date"
                          value={checkIn}
                          onChange={(e) => {
                            setCheckIn(e.target.value);
                            setSelectingDate('checkOut');
                          }}
                          onFocus={() => setSelectingDate('checkIn')}
                          onBlur={() => {
                            setTimeout(() => {
                              setSelectingDate(null);
                            }, 200);
                          }}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {/* Click area when both dates are selected - allows editing */}
                      {checkIn && checkOut && (
                        <div 
                          className="w-full pl-10 pr-10 py-3 transition-all duration-300 cursor-pointer"
                          style={{ minHeight: '48px' }}
                          onClick={(e) => {
                            if (!e.target.closest('button')) {
                              setCheckIn('');
                              setCheckOut('');
                              setSelectingDate('checkIn');
                              setTimeout(() => {
                                const checkInInput = document.getElementById('checkInModalMobile');
                                if (checkInInput) {
                                  checkInInput.focus();
                                  checkInInput.click();
                                }
                              }, 50);
                            }
                          }}
                        >
                          {/* Empty div for click area */}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop: Single Date Range Field */}
                  <div className="hidden sm:block">
                    <div 
                      className="relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                      style={{ 
                        backgroundColor: selectingDate ? 'rgba(29, 42, 59, 0.95)' : 'rgba(29, 42, 59, 0.8)',
                        border: selectingDate ? '1px solid #D5B577' : '1px solid rgba(213, 181, 119, 0.2)',
                        boxShadow: selectingDate ? '0 0 0 3px rgba(213, 181, 119, 0.1), 0 4px 12px rgba(213, 181, 119, 0.2)' : 'none',
                        transform: selectingDate ? 'scale(1.01)' : 'scale(1)',
                        minHeight: '42px',
                        padding: '10px 0'
                      }}
                      onClick={(e) => {
                        // If clicking on the container (not the clear button), trigger the appropriate input
                        if (!e.target.closest('button')) {
                          if (!checkIn) {
                            const checkInInput = document.getElementById('checkInModalDesktop');
                            if (checkInInput) {
                              checkInInput.focus();
                              checkInInput.click();
                            }
                          } else if (!checkOut) {
                            const checkOutInput = document.getElementById('checkOutModalDesktop');
                            if (checkOutInput) {
                              checkOutInput.focus();
                              checkOutInput.click();
                            }
                          } else {
                            // Both dates selected, allow editing by clicking check-in again
                            const checkInInput = document.getElementById('checkInModalDesktop');
                            if (checkInInput) {
                              checkInInput.focus();
                              checkInInput.click();
                            }
                          }
                        }
                      }}
                    >
                      <div 
                        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-all duration-300 flex items-center justify-center"
                        style={{
                          transform: selectingDate ? 'scale(1.2) rotate(5deg) translateY(-50%)' : 'scale(1) rotate(0deg) translateY(-50%)'
                        }}
                      >
                        <span className="text-base">📅</span>
                      </div>
                      {/* Display text overlay - always visible on top */}
                      <div 
                        className="absolute left-10 right-10 top-1/2 -translate-y-1/2 pointer-events-none z-50 transition-all duration-300"
                        style={{
                          opacity: selectingDate ? 0.7 : 1,
                          color: '#FFFFFF'
                        }}
                      >
                        {checkIn && checkOut ? (
                          <div className="text-sm flex items-center justify-between w-full" style={{ color: '#FFFFFF' }}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkIn)}</span>
                              <span 
                                className="transition-all duration-300"
                                style={{ 
                                  color: '#D5B577',
                                  animation: 'pulse 2s ease-in-out infinite'
                                }}
                              >→</span>
                              <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkOut)}</span>
                            </div>
                            <span className="text-xs ml-2" style={{ color: '#9CA3AF' }}>
                              {calculateNights()} {calculateNights() === 1 ? (language === 'ar' ? 'ليلة' : 'night') : (language === 'ar' ? 'ليالي' : 'nights')}
                            </span>
                          </div>
                        ) : checkIn ? (
                          <div className="text-sm flex items-center" style={{ color: '#FFFFFF' }}>
                            <span className="font-semibold" style={{ color: '#FFFFFF' }}>{formatDateRange(checkIn)}</span>
                            <span 
                              className="ml-3 text-xs animate-pulse flex items-center gap-1"
                              style={{ animation: 'pulse 1.5s ease-in-out infinite', color: '#9CA3AF' }}
                            >
                              <span>→</span>
                              <span>{language === 'ar' ? 'اختر تاريخ المغادرة' : 'Select checkout date'}</span>
                            </span>
                          </div>
                        ) : (
                          <div className="text-sm flex items-center gap-2" style={{ color: '#9CA3AF' }}>
                            <span>{language === 'ar' ? 'التواريخ' : 'Dates'}</span>
                            <span className="text-xs opacity-80">({language === 'ar' ? 'انقر للاختيار' : 'Click to select'})</span>
                          </div>
                        )}
                      </div>
                      {/* Clear button when both dates are selected */}
                      {checkIn && checkOut && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCheckIn('');
                            setCheckOut('');
                            setSelectingDate(null);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-700/80 hover:bg-red-500/80 flex items-center justify-center transition-all duration-200 z-20 group"
                          style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                          }}
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {/* Always render both inputs, always clickable - layered on top */}
                      {!checkIn && (
                        <input
                          id="checkInModalDesktop"
                          type="date"
                          value={checkIn}
                          onChange={(e) => {
                            setCheckIn(e.target.value);
                            setSelectingDate('checkOut');
                          }}
                          onFocus={() => setSelectingDate('checkIn')}
                          onBlur={() => {
                            setTimeout(() => {
                              setSelectingDate(null);
                            }, 200);
                          }}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {checkIn && !checkOut && (
                        <input
                          id="checkOutModalDesktop"
                          type="date"
                          value={checkOut}
                          onChange={(e) => {
                            setCheckOut(e.target.value);
                            setSelectingDate(null);
                          }}
                          min={checkIn || undefined}
                          onFocus={() => setSelectingDate('checkOut')}
                          onBlur={() => setSelectingDate(null)}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {checkIn && checkOut && (
                        <input
                          id="checkInModalDesktop"
                          type="date"
                          value={checkIn}
                          onChange={(e) => {
                            setCheckIn(e.target.value);
                            setSelectingDate('checkOut');
                          }}
                          onFocus={() => setSelectingDate('checkIn')}
                          onBlur={() => {
                            setTimeout(() => {
                              setSelectingDate(null);
                            }, 200);
                          }}
                          className="absolute inset-0 w-full h-full cursor-pointer z-30"
                          style={{ 
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            opacity: 0.01,
                            color: 'transparent',
                            pointerEvents: 'auto'
                          }}
                        />
                      )}
                      {/* Click area when both dates are selected - allows editing */}
                      {checkIn && checkOut && (
                        <div 
                          className="w-full pl-10 pr-10 py-2.5 transition-all duration-300 cursor-pointer"
                          style={{ minHeight: '42px' }}
                          onClick={(e) => {
                            if (!e.target.closest('button')) {
                              setCheckIn('');
                              setCheckOut('');
                              setSelectingDate('checkIn');
                              setTimeout(() => {
                                const checkInInput = document.getElementById('checkInModalDesktop');
                                if (checkInInput) {
                                  checkInInput.focus();
                                  checkInInput.click();
                                }
                              }, 50);
                            }
                          }}
                        >
                          {/* Empty div for click area */}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Guests and Property Type - Compact row on mobile */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div 
                      className="relative rounded-xl sm:rounded-lg overflow-hidden transition-all duration-300"
                      style={{ 
                        backgroundColor: 'rgba(29, 42, 59, 0.8)',
                        border: '1px solid rgba(213, 181, 119, 0.2)',
                        transform: 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                        <span className="text-base">👤</span>
                      </div>
                      <input
                        id="guestsModal"
                        type="number"
                        min="1"
                        max="20"
                        value={guests}
                        onChange={(e) => setGuests(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                        placeholder={language === 'ar' ? 'الضيوف' : 'Guests'}
                        className="w-full pl-10 px-3 py-3 sm:py-2.5 text-xs sm:text-sm text-white focus:outline-none focus:ring-2 transition-all placeholder-gray-400"
                        style={{ 
                          backgroundColor: 'transparent',
                          border: 'none'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.parentElement.style.borderColor = '#D5B577';
                          e.currentTarget.parentElement.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                          e.currentTarget.parentElement.style.transform = 'scale(1.03)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.parentElement.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                          e.currentTarget.parentElement.style.boxShadow = 'none';
                          e.currentTarget.parentElement.style.transform = 'scale(1)';
                        }}
                      />
                    </div>
                    <div 
                      className="relative rounded-xl sm:rounded-lg overflow-hidden transition-all duration-300"
                      style={{ 
                        backgroundColor: 'rgba(29, 42, 59, 0.8)',
                        border: '1px solid rgba(213, 181, 119, 0.2)',
                        transform: 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                        <span className="text-base">🏠</span>
                      </div>
                      <select
                        id="propertyTypeModal"
                        value={selectedPropertyType}
                        onChange={(e) => setSelectedPropertyType(e.target.value)}
                        className="w-full pl-10 px-3 py-3 sm:py-2.5 text-xs sm:text-sm text-white rounded-xl sm:rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none pr-8"
                        style={{ 
                          backgroundColor: 'transparent',
                          border: 'none'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.parentElement.style.borderColor = '#D5B577';
                          e.currentTarget.parentElement.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                          e.currentTarget.parentElement.style.transform = 'scale(1.03)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.parentElement.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                          e.currentTarget.parentElement.style.boxShadow = 'none';
                          e.currentTarget.parentElement.style.transform = 'scale(1)';
                        }}
                      >
                        <option value="">{language === 'ar' ? 'نوع العقار' : 'Property type'}</option>
                        {propertyTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <svg 
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Search Button - Prominent on mobile */}
                  <div className="pt-1 sm:pt-2">
                    <button
                      type="submit"
                      className="w-full py-3.5 sm:py-2.5 bg-[#D5B577] hover:bg-[#c4a366] text-[#1D2A3B] font-bold text-base sm:text-sm rounded-xl sm:rounded-lg shadow-xl hover:shadow-2xl transform transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(213, 181, 119, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      {/* Animated background effect */}
                      <div 
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                          background: 'linear-gradient(135deg, #c4a366 0%, #D5B577 100%)'
                        }}
                      ></div>
                      <svg 
                        className="w-5 h-5 sm:w-4 sm:h-4 relative z-10 transition-transform duration-300 group-hover:rotate-90" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="relative z-10">{t('home.search')}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Featured Properties */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-900" style={{ width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              {t('home.featuredProperties')}
            </h2>
            <p className="text-base sm:text-lg text-amber-100 max-w-2xl mx-auto px-4">
              {t('home.featuredProperties')}
            </p>
          </div>
          </div>

        <div className="relative">
          {/* Left Arrow - click to scroll */}
          {canScrollLeft && (
            <button
              type="button"
              onClick={scrollLeft}
              className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110 left-2 sm:left-4"
              aria-label="Scroll left"
              style={{ 
                backgroundColor: 'rgba(213, 181, 119, 0.15)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(213, 181, 119, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
              }}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right Arrow - click to scroll */}
          {canScrollRight && (
            <button
              type="button"
              onClick={scrollRight}
              className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110 right-2 sm:right-4"
              aria-label="Scroll right"
              style={{ 
                backgroundColor: 'rgba(213, 181, 119, 0.15)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(213, 181, 119, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
              }}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div 
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 scrollbar-hide"
            style={{ 
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
            }}
            onMouseDown={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grabbing';
              }
            }}
            onMouseUp={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grab';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grab';
              }
            }}
          >
            {loadingFeatured && (
              <div className="flex-shrink-0 flex items-center justify-center px-8" style={{ minWidth: '320px' }}>
                <div className="animate-pulse text-amber-200/80">{t('common.loading') || 'Loading...'}</div>
              </div>
            )}
            {!loadingFeatured && featuredProperties.length === 0 && [1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={`coming-soon-featured-${i}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex-shrink-0"
                style={{
                  width: isMobile ? '85%' : '320px',
                  minWidth: isMobile ? '280px' : '320px',
                  maxWidth: isMobile ? '85%' : '320px',
                  cursor: 'default',
                  pointerEvents: 'none',
                }}
              >
                <div
                  className="relative h-56 overflow-hidden flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)' }}
                >
                  <svg className="w-16 h-16 opacity-40" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span
                    className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded z-10"
                    style={{
                      background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                      color: '#1D2A3B',
                    }}
                  >
                    {t('properties.comingSoon')}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-500 mb-1">{t('properties.comingSoon')}</h3>
                  <p className="text-gray-400 text-sm mb-4 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    —
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">— {t('home.bedrooms')}</span>
                    <span className="flex items-center gap-1">— {t('home.bathrooms')}</span>
                    <span className="flex items-center gap-1">— m²</span>
                  </div>
                </div>
              </div>
            ))}
            {!loadingFeatured && featuredProperties.length > 0 && featuredProperties.map((property, index) => (
              <div
                key={`${property.id}-${index}`}
                onClick={(e) => {
                  // Navigate directly to property details page
                  navigate(`/properties/${property.id}`);
                }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-300 border border-gray-100 cursor-pointer select-none flex-shrink-0"
                style={{ 
                  touchAction: 'manipulation',
                  width: isMobile ? '85%' : '320px',
                  minWidth: isMobile ? '280px' : '320px',
                  maxWidth: isMobile ? '85%' : '320px',
                  transform: 'translateY(0) translateZ(0)',
                  transition: 'all 0.3s ease',
                  willChange: 'transform',
                  WebkitTransform: 'translateY(0) translateZ(0)'
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.cssText += '; transform: translateY(-8px) translateZ(0) !important; -webkit-transform: translateY(-8px) translateZ(0) !important;';
                  el.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                  if (property.video) {
                    const rect = el.getBoundingClientRect();
                    setHoveredPropertyId(property.id);
                    setHoverVideoPosition({ x: rect.right + 20, y: rect.top });
                  }
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.cssText += '; transform: translateY(0) translateZ(0) !important; -webkit-transform: translateY(0) translateZ(0) !important;';
                  el.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                  setHoveredPropertyId(null);
                }}
                onMouseMove={(e) => {
                  if (property.video && hoveredPropertyId === property.id) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoverVideoPosition({ x: rect.right + 20, y: rect.top });
                  }
                }}
              >
                <div className="relative h-56 overflow-hidden group">
                  <img
                    src={property.image}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  {property.video && (
                    <div className="absolute bottom-3 right-3 z-10">
                      <div className="w-8 h-8 rounded-full bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white border-opacity-20"
                        style={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          backdropFilter: 'blur(8px)'
                        }}
                      >
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  {property.featured && (
                    <span className="absolute top-3 left-3 bg-amber-500 text-slate-900 text-xs font-bold px-3 py-1 rounded z-10">
                      {t('exclusiveOffers.exclusive')}
                  </span>
                  )}
                  <span 
                    className="absolute bottom-3 left-3 text-white text-sm font-semibold px-3 py-1.5 rounded shadow-lg z-10"
                    style={{ 
                      background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                      color: '#1D2A3B'
                    }}
                  >
                        {formatPrice(property.price, false)} {t('home.perNight')}
                  </span>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <h3 className="text-lg font-bold text-gray-900">
                    {property.title}
                  </h3>
                    <div className="flex items-center gap-2">
                      {property.code && (
                        <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                          {property.code}
                        </span>
                      )}
                      {property.category && (
                        <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.3)' }}>
                          {property.category === 'compound' ? t('properties.inCompound') : t('properties.separate')}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {property.address || property.location}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      {property.beds} {t('home.bedrooms')}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      {property.baths} {t('home.bathrooms')}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      {property.sqft ? Math.round(property.sqft * 0.092903) : 'N/A'} m²
                    </span>
                  </div>
                  <div className="flex items-center justify-end">
                    <div className="flex gap-2">
                  <button
                        onClick={(e) => handleShare(property.id, e)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors relative"
                        aria-label="Share"
                      >
                        {copiedPropertyId === property.id ? (
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                            {t('common.copied')}
                          </span>
                        ) : null}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (user) {
                            const id = String(property.id ?? property._id ?? '');
                            const currentlySaved = savedProperties.has(id);
                            toggleSavedPropertyAPI(id, currentlySaved)
                              .then((newSaved) => {
                                setSavedProperties(prev => {
                                  const next = new Set(prev);
                                  if (newSaved) next.add(id); else next.delete(id);
                                  return next;
                                });
                              })
                              .catch(() => {});
                          } else {
                            navigate('/login', { state: { redirectTo: '/client', redirectView: 'saved' } });
                          }
                        }}
                        className={`p-2 transition-colors ${
                          user && savedProperties.has(String(property.id ?? property._id ?? ''))
                            ? 'text-red-600'
                            : 'text-gray-400 hover:text-red-600'
                        }`}
                        aria-label={user && savedProperties.has(String(property.id ?? property._id ?? '')) ? t('common.unlike') : t('common.like')}
                      >
                        <svg 
                          className="w-4 h-4" 
                          fill={user && savedProperties.has(String(property.id ?? property._id ?? '')) ? 'currentColor' : 'none'} 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                  >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                  </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Hover Video Preview */}
        {hoveredPropertyId && (() => {
          const property = featuredProperties.find(p => p.id === hoveredPropertyId && p.video);
          if (!property) return null;
          
          // Calculate position to prevent going off-screen
          const videoWidth = 320;
          const videoHeight = 180;
          const padding = 20;
          let { x, y } = hoverVideoPosition;
          
          // Adjust if video would go off right edge
          if (x + videoWidth + padding > window.innerWidth) {
            x = window.innerWidth - videoWidth - padding;
          }
          
          // Adjust if video would go off bottom edge
          if (y + videoHeight / 2 + padding > window.innerHeight) {
            y = window.innerHeight - videoHeight / 2 - padding;
          }
          
          // Adjust if video would go off top edge
          if (y - videoHeight / 2 < padding) {
            y = videoHeight / 2 + padding;
          }
          
          return (
            <div
              ref={hoverVideoRef}
              className="fixed z-50 pointer-events-none"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                width: `${videoWidth}px`,
                height: `${videoHeight}px`,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                backgroundColor: '#000',
                transform: 'translateY(-50%)',
                transition: 'opacity 0.2s ease-in-out'
              }}
            >
              <video
                src={typeof property.video === 'string' ? property.video : (property.videos && property.videos[0]) || ''}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ pointerEvents: 'none' }}
              />
            </div>
          );
        })()}
      </section>

      {/* Why Us Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'rgba(29, 42, 59, 0.9)', width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 px-2">
              {t('about.whyChooseUs')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl mx-auto px-4">
              {t('home.whyChooseUsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {[
              {
                icon: (
                  <svg
                    className="w-full h-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                ),
                title: t('about.whyChooseUsItems.verifiedProperties'),
                description: t('home.whyChooseUsItems.verifiedPropertiesDesc'),
              },
              {
                icon: (
                  <svg
                    className="w-full h-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                ),
                title: t('home.whyChooseUsItems.securePayments'),
                description: t('home.whyChooseUsItems.securePaymentsDesc'),
              },
              {
                icon: (
                  <svg
                    className="w-full h-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ),
                title: t('about.whyChooseUsItems.professionalService'),
                description: t('home.whyChooseUsItems.supportDesc'),
              },
              {
                icon: (
                  <svg
                    className="w-full h-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
                title: t('about.whyChooseUsItems.easyBooking'),
                description: t('about.whyChooseUsItems.easyBookingDesc'),
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 sm:p-8 rounded-2xl bg-slate-700 hover:bg-slate-600 transition-all duration-300 transform hover:-translate-y-2 border border-amber-500/30 w-full"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-amber-500/20 rounded-full text-amber-400 mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-12 sm:h-12">
                  {feature.icon}
                </div>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" style={{ width: '100%', minHeight: 'auto', overflow: 'hidden' }}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('about.whatPeopleThink')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('about.subtitle')}
            </p>
          </div>
        </div>

        <div className="relative" style={{ direction: 'ltr' }}>
          {/* Left Arrow - click to scroll */}
          {canScrollLeftTestimonials && (
            <button
              type="button"
              onClick={scrollLeftTestimonials}
              className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110 left-2 sm:left-4"
              aria-label="Scroll left"
              style={{ 
                backgroundColor: 'rgba(213, 181, 119, 0.15)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(213, 181, 119, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
              }}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right Arrow - click to scroll */}
          {canScrollRightTestimonials && (
            <button
              type="button"
              onClick={scrollRightTestimonials}
              className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110 right-2 sm:right-4"
              aria-label="Scroll right"
              style={{ 
                backgroundColor: 'rgba(213, 181, 119, 0.15)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(213, 181, 119, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
              }}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div 
            ref={testimonialsScrollRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide"
            style={{ 
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              direction: 'ltr'
            }}
            onMouseDown={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grabbing';
              }
            }}
            onMouseUp={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grab';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget && window.innerWidth >= 768) {
                e.currentTarget.style.cursor = 'grab';
              }
            }}
          >
            {(() => {
              const testimonials = [
                {
                  id: 1,
                  name: t('about.testimonials.1.name'),
                  role: t('about.testimonials.1.role'),
                  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80',
                  quote: t('about.testimonials.1.quote'),
                },
                {
                  id: 2,
                  name: t('about.testimonials.2.name'),
                  role: t('about.testimonials.2.role'),
                  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80',
                  quote: t('about.testimonials.2.quote'),
                },
                {
                  id: 3,
                  name: t('about.testimonials.3.name'),
                  role: t('about.testimonials.3.role'),
                  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80',
                  quote: t('about.testimonials.3.quote'),
                },
                {
                  id: 4,
                  name: t('about.testimonials.4.name'),
                  role: t('about.testimonials.4.role'),
                  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80',
                  quote: t('about.testimonials.4.quote'),
                },
                {
                  id: 5,
                  name: t('about.testimonials.5.name'),
                  role: t('about.testimonials.5.role'),
                  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&q=80',
                  quote: t('about.testimonials.5.quote'),
                },
                {
                  id: 6,
                  name: t('about.testimonials.6.name'),
                  role: t('about.testimonials.6.role'),
                  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80',
                  quote: t('about.testimonials.6.quote'),
                },
                {
                  id: 7,
                  name: t('about.testimonials.7.name'),
                  role: t('about.testimonials.7.role'),
                  image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&q=80',
                  quote: t('about.testimonials.7.quote'),
                },
                {
                  id: 8,
                  name: t('about.testimonials.8.name'),
                  role: t('about.testimonials.8.role'),
                  image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&q=80',
                  quote: t('about.testimonials.8.quote'),
                },
              ];
              // Only duplicate on desktop for scrolling, use single set on mobile
              // Duplicate for seamless scrolling on both mobile and desktop
              // No duplication - use arrow navigation instead
              return testimonials;
            })().map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-amber-500/20 transition-all duration-300 transform hover:-translate-y-2 border border-amber-500/30 relative flex-shrink-0"
                style={{
                  width: isMobile ? '85%' : '380px',
                  minWidth: isMobile ? '300px' : '380px',
                  maxWidth: isMobile ? '85%' : '380px'
                }}
              >
                <div className="relative z-10">
                  <p className="text-gray-200 mb-6 leading-relaxed text-base">
                    {testimonial.quote}
                  </p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-4 border-amber-500/50"
                    />
                    <div>
                      <h4 className="font-bold text-white">{testimonial.name}</h4>
                      <p className="text-sm text-amber-200">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* List Your Property Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(to right, #D5B577, #c4a366)', width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('home.listPropertyTitle')}
          </h2>
          <p className="text-xl text-white mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            {t('home.listPropertyText')}
              </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/register-owner')}
              className="px-8 py-4 font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
              style={{ backgroundColor: '#1D2A3B', color: '#D5B577' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2a3d56'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1D2A3B'}
            >
              {t('home.becomePartner')}
            </button>
            </div>
            </div>
      </section>

      {/* Footer */}
      <Footer />

    </div>
    </>
  );
};

export default Home;

// Add dynamic animations for search modal
if (typeof document !== 'undefined') {
  const styleId = 'home-search-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes slideDown {
        from {
          transform: translateY(-10px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes scaleIn {
        from {
          transform: scale(0.95);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
      
      .animate-fade-in {
        animation: fadeIn 0.2s ease-out;
      }
      
      .animate-slide-down {
        animation: slideDown 0.2s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
}

